import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line, Legend } from 'recharts';
import { extractLogsService } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import { formatDay } from '../../utils/format';

const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1'];

function TopLicenseKeysChart({ days = 7, facilityFilter = '', onLicenseKeyClick = null, selectedDate = '', filteredRuns = [], selectedLicenseKey = '' }) {
    const [data, setData] = useState([]);
    const [timelineData, setTimelineData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [skippedRuns, setSkippedRuns] = useState(0);
    const [viewMode, setViewMode] = useState('bar'); // 'bar' or 'timeline'

    useEffect(() => {
        loadData();
    }, [days, facilityFilter, selectedDate, filteredRuns]);

    const loadData = async () => {
        let skipped = 0;
        try {
            setLoading(true);
            setError(null);
            
            // If a specific date is selected, compute from filteredRuns
            if (selectedDate && filteredRuns.length > 0) {
                const completedRuns = filteredRuns.filter(r => r.status === 'Complete');
                
                // Fetch details for each run on the selected date
                const licenseKeyTotals = {};
                
                for (const run of completedRuns) {
                    try {
                        const detail = await extractLogsService.getRunDetail(run.runID);
                        detail.details.forEach(d => {
                            if (!licenseKeyTotals[d.licenseKey]) {
                                licenseKeyTotals[d.licenseKey] = {
                                    licenseKey: d.licenseKey,
                                    facilityID: run.facilityID,
                                    totalMins: 0,
                                    totalRows: 0,
                                    runCount: 0
                                };
                            }
                            licenseKeyTotals[d.licenseKey].totalMins += d.totalMins || 0;
                            licenseKeyTotals[d.licenseKey].totalRows += d.totalRows || 0;
                            licenseKeyTotals[d.licenseKey].runCount += 1;
                        });
                    } catch {
                        skipped++;
                    }
                }
                
                // Sort by totalMins and take top 10
                const sorted = Object.values(licenseKeyTotals)
                    .sort((a, b) => b.totalMins - a.totalMins)
                    .slice(0, 10);
                
                const chartData = sorted.map((item, index) => ({
                    name: item.licenseKey,
                    shortName: item.licenseKey.slice(-8),
                    facility: item.facilityID,
                    totalMins: Math.round(item.totalMins * 100) / 100,
                    totalRows: item.totalRows,
                    runCount: item.runCount,
                    color: COLORS[index % COLORS.length]
                }));
                
                setData(chartData);
                setTimelineData([]);
            } else {
                // Use API endpoint for aggregate data
                const result = await extractLogsService.getTopLicenseKeys(days, facilityFilter || null, 10);
                
                const chartData = result.map((item, index) => ({
                    name: item.licenseKey,
                    shortName: item.licenseKey.slice(-8),
                    facility: item.facilityID,
                    totalMins: Math.round(item.totalMins * 100) / 100,
                    totalRows: item.totalRows,
                    runCount: item.runCount,
                    color: COLORS[index % COLORS.length]
                }));
                
                setData(chartData);

                // Get timeline data for top 5 license keys
                if (chartData.length > 0) {
                    const topKeys = chartData.slice(0, 5).map(d => d.name);
                    const runs = await extractLogsService.getRuns(days, facilityFilter || null);
                    const completeRuns = runs.filter(r => r.status === 'Complete');
                    
                    // Fetch details for each run to get license key durations
                    const timelineMap = {};
                    
                    for (const run of completeRuns.slice(0, 20)) { // Limit to recent 20 runs for performance
                        try {
                            const detail = await extractLogsService.getRunDetail(run.runID);
                            const date = formatDay(run.startTime);
                            
                            if (!timelineMap[date]) {
                                timelineMap[date] = { date };
                            }
                            
                            detail.details.forEach(d => {
                                if (topKeys.includes(d.licenseKey)) {
                                    const key = d.licenseKey.slice(-8);
                                    if (!timelineMap[date][key]) {
                                        timelineMap[date][key] = 0;
                                    }
                                    timelineMap[date][key] += d.totalMins || 0;
                                }
                            });
                        } catch {
                            skipped++;
                        }
                    }
                    
                    const timeline = Object.values(timelineMap)
                        .sort((a, b) => new Date(a.date) - new Date(b.date));
                    
                    setTimelineData(timeline);
                }
            }
        } catch (err) {
            setError(`Couldn't load top license keys: ${getErrorMessage(err)}`);
        } finally {
            setSkippedRuns(skipped);
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="chart-loading">Loading top license keys...</div>;
    }

    if (error) {
        return <div className="chart-empty">{error}</div>;
    }

    if (data.length === 0) {
        return <div className="chart-empty">No data available</div>;
    }

    const skippedNote = skippedRuns > 0 && (
        <p className="chart-warning">
            ⚠️ {skippedRuns} run{skippedRuns > 1 ? 's' : ''} couldn't be loaded, so this chart may be incomplete.
        </p>
    );

    const top5Keys = data.slice(0, 5).map(d => d.shortName);

    return (
        <div className="chart-container">
            <div className="chart-header">
                <h3 className="chart-title">Top 10 Slowest License Keys</h3>
                <div className="chart-toggle">
                    <button 
                        className={`toggle-btn ${viewMode === 'bar' ? 'active' : ''}`}
                        onClick={() => setViewMode('bar')}
                    >
                        Total
                    </button>
                    <button 
                        className={`toggle-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                        onClick={() => setViewMode('timeline')}
                    >
                        Timeline
                    </button>
                </div>
            </div>

            {viewMode === 'bar' ? (
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                        data={data} 
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                            type="number"
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                            label={{ value: 'Total Minutes', position: 'insideBottom', offset: -5, fill: '#a1a1aa' }}
                        />
                        <YAxis 
                            type="category"
                            dataKey="shortName"
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 11 }}
                            width={90}
                        />
                        <Tooltip 
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const item = payload[0].payload;
                                    return (
                                        <div style={{ 
                                            background: 'rgba(26, 26, 46, 0.95)', 
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            color: '#e4e4e7'
                                        }}>
                                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{item.name}</div>
                                            <div style={{ fontSize: '12px', color: '#a1a1aa', marginBottom: '8px' }}>{item.facility}</div>
                                            <div style={{ fontSize: '13px' }}>Duration: <strong>{item.totalMins} min</strong></div>
                                            <div style={{ fontSize: '13px' }}>Rows: <strong>{item.totalRows.toLocaleString()}</strong></div>
                                            <div style={{ fontSize: '13px' }}>Runs: <strong>{item.runCount}</strong></div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Bar 
                            dataKey="totalMins" 
                            radius={[0, 4, 4, 0]}
                            onClick={(data) => {
                                if (onLicenseKeyClick && data && data.name) {
                                    onLicenseKeyClick(data.name);
                                }
                            }}
                            style={{ cursor: onLicenseKeyClick ? 'pointer' : 'default' }}
                        >
                            {data.map((entry, index) => (
                                <Cell 
                                    key={`cell-${index}`} 
                                    fill={entry.color}
                                    opacity={selectedLicenseKey && selectedLicenseKey !== entry.name ? 0.3 : 1}
                                    stroke={selectedLicenseKey === entry.name ? '#fff' : 'none'}
                                    strokeWidth={selectedLicenseKey === entry.name ? 2 : 0}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={timelineData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                            dataKey="date" 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 11 }}
                            tickFormatter={(value) => {
                                const date = new Date(value);
                                return `${date.getMonth() + 1}/${date.getDate()}`;
                            }}
                        />
                        <YAxis 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#a1a1aa' }}
                        />
                        <Tooltip 
                            contentStyle={{ 
                                background: 'rgba(26, 26, 46, 0.95)', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                                color: '#e4e4e7'
                            }}
                            formatter={(value) => [`${value?.toFixed(2) || 0} min`, '']}
                        />
                        <Legend 
                            wrapperStyle={{ color: '#e4e4e7' }}
                            formatter={(value) => <span style={{ color: '#e4e4e7' }}>{value}</span>}
                        />
                        {top5Keys.map((key, index) => (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={COLORS[index]}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                connectNulls
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            )}
            {viewMode === 'timeline' && (
                <p className="chart-hint">Showing top 5 slowest license keys over time</p>
            )}
            {skippedNote}
        </div>
    );
}

export default TopLicenseKeysChart;
